import * as React from "react"
import { fromJSX, Redirect, Updater, Widget } from "widgets-for-react"
import { RootLayout } from "../../site/root/rootLayout";
import { ComponentDidMount } from "../../utils/lifecycleMethods";
import { Api } from "./api_to_state";
import { ActivateAccountState, AuthState, isActivateAccountState } from "./authState"
import { formSkeletonConfig, FormSkeletonLayout } from "../../utils/forms";
import { getClassName, options } from "./utils"
import i18next from "i18next";

export const activateAccountWidget: Widget<Updater<AuthState>> = fromJSX(
  (setState) => (
    <>
      <ComponentDidMount
        onMount={() =>
          setState((s) => ({
            ...s,
            activateAccountState: 'activate account - activating',
          }))
        }
      />
      <RootLayout.PageLoader />
    </>
  )
)

function handlePasswordActivate(e: React.FormEvent<HTMLFormElement>, setState: (_: Updater<AuthState>) => void) {
  e.preventDefault();
  setState(s => ({ ...s, activateAccountState: "activate account - changing password" })
  );
}

export function activateAndChangePasswordForm(options: options, s0: ActivateAccountState, readonly: boolean, email: string, api: Api): Widget<Updater<AuthState>> {
  let passwordInputType = s0.activateAccountData.showPassword ? "text" : "password"
  let password_score = api.scorePassword(s0.activateAccountData.newPassword || '').res
  let new_password_error = (s0.activateAccountData.newPassword != undefined && s0.activateAccountData.newPassword != '' && (password_score != "strong"))
    || s0.activateAccountState == "activate account - error weak password";
  let new_password_confirmed_error = (s0.activateAccountData.newPasswordConfirmed != undefined && s0.activateAccountData.newPassword != s0.activateAccountData.newPasswordConfirmed);

  return fromJSX((setState: (_: Updater<AuthState>) => void) => (
    <section className={getClassName(options, "blockClassName", "container")} key={options && options.key ? options.key : "login-widget-activate-account-form"}>
      <div className={getClassName(options, "innerClassName", "py-5")}>
        <header className={getClassName(options, "headerClassName", "")}>
          <h1 className={getClassName(options, "titleClassName", "mb-3")}>{i18next.t("auth:activateHeader")}</h1>
          <p className={getClassName(options, "introClassName", "")}>{i18next.t("auth:activateChangePassword")}</p>
          <p className={getClassName(options, "introClassName", "font-weight-bold mb-3")}>{email}</p>
        </header>

        <form onSubmit={(e) => handlePasswordActivate(e, setState)} className={getClassName(options, "formClassName", "")}>

          <div className={getClassName(options, "formInnerClassName", "")}>

            {/* New password */}
            <div className={getClassName(options, "formGroupClassName", "form-group")}>
              <label
                htmlFor="new_password"
                className={getClassName(options, "formLabelClassName", "")}
              >
                {i18next.t("auth:newPassword")}
              </label>
              <input
                type={passwordInputType}
                name="new_password"
                id="new_password"
                aria-invalid={new_password_error}
                aria-describedby={new_password_error ? "a11y-new-password-error" : undefined}
                className={getClassName(options, "formInputClassName", `form-control ${new_password_error ? "is-invalid" : ""}`)}
                value={s0.activateAccountData.newPassword || ""}
                disabled={readonly}
                onChange={(event) => setState(s => isActivateAccountState(s) ? ({
                  ...s,
                  activateAccountData: {
                    ...s.activateAccountData,
                    newPassword: event.currentTarget.value
                  }
                }) : s)
                }
              />
              {new_password_error && (
                <p id="a11y-new-password-error" className={getClassName(options, "formFieldFeedbackInvalidClassName", "invalid-feedback")}>
                  {i18next.t("auth:weakPassword")}
                </p>
              )}
            </div>

            {/* Repeat new password */}
            <div className={getClassName(options, "formGroupClassName", "form-group")}>
              <label
                htmlFor="new_password_confirmed"
                className={getClassName(options, "formLabelClassName", "")}
              >
                {i18next.t("auth:confirmNewPassword")}
              </label>
              <input
                type={passwordInputType}
                name="new_password_confirmed"
                id="new_password_confirmed"
                aria-invalid={new_password_confirmed_error}
                aria-describedby={new_password_confirmed_error ? "a11y-new-password-confirmed-error" : undefined}
                className={getClassName(options, "formInputClassName", `form-control ${new_password_confirmed_error ? "is-invalid" : ""}`)}
                value={s0.activateAccountData.newPasswordConfirmed || ""}
                disabled={readonly}
                onChange={(event) => setState(s => isActivateAccountState(s) ? ({
                  ...s,
                  activateAccountData: {
                    ...s.activateAccountData,
                    newPasswordConfirmed: event.currentTarget.value
                  }
                }) : s)}
              />
              {new_password_confirmed_error && (
                <p id="a11y-new-password-confirmed-error" className={getClassName(options, "formFieldFeedbackInvalidClassName", "invalid-feedback")}>
                  {i18next.t("auth:mismatchError")}
                </p>
              )}
            </div>

            {/* Show password toggle */}
            <div className={getClassName(options, "formGroupClassName", "form-group form-check")}>
              <input
                type="checkbox"
                name="show_password"
                id="show_password"
                tabIndex={-1}
                className={getClassName(options, "formInputClassName", "form-check-input")}
                checked={s0.activateAccountData.showPassword}
                disabled={readonly}
                onChange={(event) => setState(s => isActivateAccountState(s) ? ({
                  ...s,
                  activateAccountData: {
                    ...s.activateAccountData,
                    showPassword: event.currentTarget.checked
                  }
                }) : s)}
              />
              <label
                htmlFor="show_password"
                className={getClassName(options, "formLabelClassName", "form-check-label")}
              >
                {i18next.t("auth:showPassword")}
              </label>
            </div>

            {/* Submit */}
            <div className={getClassName(options, "formActionsClassName", "")}>
              <button
                type="submit"
                className={getClassName(options, "clickablePrimaryClassName", "btn btn-dark")}
                disabled={readonly || new_password_error || new_password_confirmed_error}
              >
                {i18next.t("auth:activateSaveButton")}
              </button>

            </div>
          </div>
        </form>
      </div>
    </section>
  ))
}

export const activateAccountSuccess = (originPath: string, options: options): Widget<Updater<AuthState>> =>
  fromJSX(() => (
    <Redirect
      to={i18next.language == 'en'
        ? '/en/login?originPath=' + originPath + '?signup-success=true'
        : '/nl/login?originPath=' + originPath + '?signup-success=true'
      }
    />
  ))

export function activate_account_failed(options: options): Widget<Updater<AuthState>> {
  return fromJSX(() => (
    <FormSkeletonLayout
      type="activate-account-failed"
      kind="text-image"
      options={options}
      image={formSkeletonConfig.imageLight}
    >
      <header className={getClassName(options, "headerClassName", "mb-4")}>
        <h1 className={getClassName(options, "titleClassName", "mb-4")}>{i18next.t("auth:oops")}</h1>
        <p className={getClassName(options, "introClassName", "lead")}>{i18next.t("auth:activateNoToken")}</p>
      </header>
    </FormSkeletonLayout>
  ))
}
